import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { STAGE_LABELS, leadInfo, telLink, waLink, ago } from "@/lib/leads";
import { LeadDetail, useMarkContacted } from "@/components/pawkt/LeadDetail";
import { Inbox, Phone, MessageCircle, Check } from "lucide-react";

function LeadCard({ l, onOpen, onContacted }) {
  const i = leadInfo(l);
  return (
    <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-3" data-testid={`new-lead-${l.id}`}>
      <button onClick={() => onOpen(l)} className="block w-full min-w-0 text-left" data-testid={`new-lead-view-${l.id}`}>
        <p className="truncate font-semibold text-slate-900">{l.name}{i.pet ? <span className="font-normal text-slate-500"> · {i.pet}{i.species ? ` (${i.species})` : ""}</span> : null}</p>
        <p className="truncate text-xs text-slate-500">{[l.mobile, i.service, i.source, ago(l.created_at), STAGE_LABELS[l.stage]].filter(Boolean).join(" · ")}</p>
      </button>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <button onClick={() => onOpen(l)} className="rounded-full border px-2.5 py-1 text-xs font-semibold" data-testid={`new-lead-open-${l.id}`}>View</button>
        {l.mobile && <a href={telLink(l.mobile)} className="flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold" data-testid={`new-lead-call-${l.id}`}><Phone className="h-3 w-3" /> Call</a>}
        {l.mobile && <a href={waLink(l.mobile)} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-full border border-emerald-300 px-2.5 py-1 text-xs font-semibold text-emerald-700" data-testid={`new-lead-wa-${l.id}`}><MessageCircle className="h-3 w-3" /> WhatsApp</a>}
        <button onClick={() => onContacted(l)} className="flex items-center gap-1 rounded-full bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white" data-testid={`new-lead-contacted-${l.id}`}><Check className="h-3 w-3" /> Mark contacted</button>
      </div>
    </div>
  );
}

export function NewLeadsCard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(null);
  const contacted = useMarkContacted();
  const allowed = hasPerm(user, "leads.view");
  const { data } = useQuery({ queryKey: ["leads-inbox"], enabled: allowed, refetchInterval: 30000, queryFn: async () => (await api.get("/leads/inbox")).data });
  if (!allowed || !data) return null;
  if (!data.new_count && !data.overdue_count && !data.new?.length && !data.follow_ups?.length) return null;
  return (
    <section className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4" data-testid="new-leads-card">
      <div className="mb-3 flex items-center justify-between">
        <p className="flex items-center gap-2 font-display font-bold text-slate-900"><Inbox className="h-4 w-4 text-violet-600" /> New Leads
          <span className="rounded-full bg-violet-600 px-2 text-xs text-white" data-testid="new-leads-count">{data.new_count}</span>
          {data.overdue_count > 0 && <span className="rounded-full bg-rose-100 px-2 text-xs text-rose-700" data-testid="leads-overdue-count">{data.overdue_count} overdue follow-up{data.overdue_count > 1 ? "s" : ""}</span>}</p>
        <button onClick={() => nav("/leads")} className="text-xs font-semibold text-violet-700" data-testid="new-leads-all">All leads</button>
      </div>
      {data.new.length ? (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">{data.new.map((l) => <LeadCard key={l.id} l={l} onOpen={setOpen} onContacted={contacted} />)}</div>
      ) : (
        <div data-testid="new-leads-empty">
          <p className="text-sm text-slate-600">No new leads. You're all caught up.</p>
          {data.follow_ups.length > 0 && <ul className="mt-2 space-y-1">{data.follow_ups.map((l) => (
            <li key={l.id}><button onClick={() => setOpen(l)} className="text-left text-xs" data-testid={`lead-followup-${l.id}`}>
              <span className={l.overdue ? "font-semibold text-rose-700" : "text-slate-700"}>{l.overdue ? "Overdue · " : ""}{l.next_follow_up.replace("T", " ").slice(0, 16)}</span> · {l.name}</button></li>))}</ul>}
        </div>
      )}
      {open && <LeadDetail lead={open} onClose={() => setOpen(null)} />}
    </section>
  );
}
